import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'

import { AuthProvider } from '@/hooks/useAuth'
import { Auth0Provider } from '@/hooks/useAuth0'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import Layout from '@/components/layout/Layout'
import PageLoadingFallback from '@/components/common/PageLoadingFallback'
import ErrorBoundary from '@/components/common/ErrorBoundary'
import { ReportingPeriodProvider } from '@/contexts/ReportingPeriodContext'
import { SidebarThemeProvider } from '@/contexts/sidebar-theme-context'
import { TimePeriodProvider } from '@/hooks/use-time-period'
import { Permission } from '@/types/auth'
import { performanceHubRedirects } from '@/routes/performanceHubRedirects'
import { configureHubRedirects } from '@/routes/configureHubRedirects'

// ---------------------------------------------------------------------------
// Auth pages - kept eager (entry points)
// ---------------------------------------------------------------------------
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import CallbackPage from '@/pages/auth/CallbackPage'
import AccountNotFoundPage from '@/pages/AccountNotFoundPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import NotFoundPage from '@/pages/NotFoundPage'

// Dashboard
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const CommandCentrePage = lazy(() => import('@/pages/CommandCentrePage'))

// IBS
const IBSDetailPage = lazy(() => import('@/pages/ibs/IBSDetailPage'))
const IBSEditWizard = lazy(() => import('@/pages/ibs/IBSEditWizard'))
const IBSMicroservicesPage = lazy(() => import('./pages/ibs/IBSMicroservicesPage'))
const FlowViewPage = lazy(() => import('./pages/ibs/FlowViewPage'))
const FlowBuilderWizard = lazy(() => import('./pages/ibs/FlowBuilderWizard'))
const IBSCreateWizard = lazy(() => import('./pages/ibs/ibs-wizard/IBSCreateWizard'))
const IBSTreeView = lazy(() => import('./pages/ibs/IBSTreeView'))
const MicroserviceDetailPage = lazy(() => import('./pages/ibs/MicroserviceDetailPage'))
const TestMicroservicesPage = lazy(() => import('./pages/ibs/TestMicroservicesPage'))

// Performance Hub (OR-74)
const PerformanceHubIBSPage = lazy(() => import('@/pages/performanceHub/PerformanceHubIBSPage'))
const PerformanceHubOperationalComponentsPage = lazy(() => import('@/pages/performanceHub/PerformanceHubOperationalComponentsPage'))
const PerformanceDataSubmissionPage = lazy(() => import('@/pages/performanceHub/PerformanceDataSubmissionPage'))

// Configure Hub (OR-74 BR-002)
const ConfigureComponentsPage = lazy(() => import('@/pages/configureHub/ConfigureComponentsPage'))
const ConfigureIBSPage = lazy(() => import('@/pages/configureHub/ConfigureIBSPage'))

// Microservices (sub-routes only; the listing is rendered via PerformanceHubOperationalComponentsPage)
const MicroservicePerformanceView = lazy(() => import('./pages/microservices/MicroservicePerformanceView'))
const MicroserviceFormPage = lazy(() => import('./pages/microservices/MicroserviceFormPage'))
const MicroserviceRegistryDetailPage = lazy(() => import('./pages/microservices/MicroserviceDetailPage'))

// Systems (sub-routes only)
const SystemPerformanceView = lazy(() => import('./pages/systems/SystemPerformanceView'))
const SystemForm = lazy(() => import('@/pages/systemsRegistry/SystemForm'))
const SystemDetailPage = lazy(() => import('@/pages/systemsRegistry/SystemDetailPage'))

// Infrastructure (sub-routes only)
const InfrastructurePerformanceView = lazy(() => import('./pages/infrastructure/InfrastructurePerformanceView'))
const InfrastructureForm = lazy(() => import('@/pages/infrastructureRegistry/InfrastructureForm'))
const InfrastructureDetailPage = lazy(() => import('@/pages/infrastructureRegistry/InfrastructureDetailPage'))

// Teams (sub-routes only)
const TeamFormPage = lazy(() => import('@/pages/teamsRegistry/TeamFormPage'))
const TeamDetailPage = lazy(() => import('@/pages/teamsRegistry/TeamDetailPage'))

// Suppliers (sub-routes only)
const SupplierFormPage = lazy(() => import('@/pages/suppliersRegistry/SupplierFormPage'))
const SupplierDetailPage = lazy(() => import('@/pages/suppliers/SupplierDetailPage'))
const SupplierPerformanceView = lazy(() => import('@/pages/suppliers/SupplierPerformanceView'))

// Locations / Critical Data / Relationships (detail-only)
const LocationDetailPage = lazy(() => import('@/pages/locationsRegistry/LocationDetailPage'))
const CriticalDataDetailPage = lazy(() => import('@/pages/criticalData/CriticalDataDetailPage'))
const RelationshipDetailPage = lazy(() => import('@/pages/relationships/RelationshipDetailPage'))

// IBS Registry


// Scenarios
const EnhancedScenariosPage = lazy(() => import('./pages/scenarios/EnhancedScenariosPage'))
const EnhancedScenarioWizard = lazy(() => import('./pages/scenarios/EnhancedScenarioWizard'))
const CreateScenarioPage = lazy(() => import('./pages/scenarios/CreateScenarioPage'))
const IBSAnalysisSelectionPage = lazy(() => import('./pages/scenarios/IBSAnalysisSelectionPage'))

// Risk / Controls
const RisksPage = lazy(() => import('@/pages/risks/RisksPage'))
const ControlsPage = lazy(() => import('@/pages/controls/ControlsPage'))
const TreatmentActionsPage = lazy(() => import('@/pages/treatment-actions/TreatmentActionsPage'))
const IncidentsPage = lazy(() => import('@/pages/incidents/IncidentsPage'))

// CMORG Compliance Pages
const ScenarioLibraryPage = lazy(() => import('@/pages/scenarios/ScenarioLibraryPage'))
const CMORGLibraryPage = lazy(() => import('@/pages/scenarios/CMORGLibraryPage'))
const VulnerabilitiesPage = lazy(() => import('@/pages/vulnerabilities/VulnerabilitiesPage'))
const VulnerabilityDetailPage = lazy(() => import('@/pages/vulnerabilities/VulnerabilityDetailPage'))
const LessonsLearnedPage = lazy(() => import('@/pages/lessons/LessonsLearnedPage'))
const SelfAssessmentPage = lazy(() => import('@/pages/selfAssessment/SelfAssessmentPage'))

// CMORG Gap Analysis Pages
const ResourceMappingPage = lazy(() => import('@/pages/resources/ResourceMappingPage'))
const IBSResourceMapPage = lazy(() => import('@/pages/resources/IBSResourceMapPage'))
const ConcentrationRiskPage = lazy(() => import('@/pages/resources/ConcentrationRiskPage'))
const CausationImpactMatrixPage = lazy(() => import('@/pages/scenarios/CausationImpactMatrixPage'))
const ScenarioDetailPage = lazy(() => import('@/pages/scenarios/ScenarioDetailPage'))

// Knowledge Hub (Vault) - uses shared @profylr/vault-ui package
const VaultPage = lazy(() => import('@/pages/vault/VaultPage'))
const DocumentDetailPage = lazy(() => import('@/pages/vault/DocumentDetailPage'))
const DocumentDiffPage = lazy(() => import('@/pages/vault/DocumentDiffPage'))

// Documents / Reports / Settings
const IntegrationsPage = lazy(() => import('@/pages/integrations/IntegrationsPage'))
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'))
const PerformanceMonitoringForm = lazy(() => import('@/pages/monitoring/PerformanceMonitoringForm'))
const OrganizationResourceMap = lazy(() => import('@/pages/OrganizationResourceMap'))
const HelpPage = lazy(() => import('@/pages/HelpPage'))

// Client Admin / Activation
const ActivateAdminPage = lazy(() => import('@/pages/activation/ActivateAdmin'))
const UserManagementPage = lazy(() => import('@/pages/admin/UserManagement'))
const RoleManagementPage = lazy(() => import('@/pages/admin/RoleManagement'))

// Admin
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'))
// Admin component-config pages are mounted via ConfigureComponentsPage (OR-74 BR-002).
const SystemPerformancePage = lazy(() => import('@/pages/admin/SystemPerformancePage'))
const MicroservicePerformancePage = lazy(() => import('@/pages/admin/MicroservicePerformancePage'))
const InfrastructurePerformancePage = lazy(() => import('@/pages/admin/InfrastructurePerformancePage'))
const AISettingsPage = lazy(() => import('@/pages/admin/AISettingsPage'))





// Scenario / Supplier / Resources
const ScenarioDefinitionPage = lazy(() => import('@/pages/scenarioDefinition/ScenarioDefinitionPage'))
const SupplierAlternatePage = lazy(() => import('@/pages/supplierAlternate/SupplierAlternatePage'))
const ResourceLinkagePage = lazy(() => import('@/pages/resources/ResourceLinkagePage'))

// CMORG KRI & Critical Data Elements (the listing is rendered via PerformanceHubOperationalComponentsPage)
const CriticalDataFormPage = lazy(() => import('@/pages/criticalData/CriticalDataFormPage'))

/**
 * OR-323: the standalone /ibs/:hubIbsId/flows page was retired; this
 * redirect preserves bookmarks by sending them to the IBS detail page,
 * which already exposes per-IBS flows via its E2E Flows tab.
 */
function RedirectIBSFlowsToDetail() {
  const { hubIbsId } = useParams<{ hubIbsId: string }>()
  return <Navigate to={`/ibs/${hubIbsId}`} replace />
}

/** Helper to wrap a page component with Suspense and an ErrorBoundary */
function P({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary level="route">
      <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <SidebarThemeProvider>
      <TimePeriodProvider>
        <AuthProvider>
          <Auth0Provider>
            <ReportingPeriodProvider>
              <ErrorBoundary level="route">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/callback" element={<CallbackPage />} />
              <Route path="/account-not-found" element={<AccountNotFoundPage />} />

              {/* Protected routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<P><DashboardPage /></P>} />
            <Route path="command-centre" element={<P><CommandCentrePage /></P>} />
            {/* Performance Hub (OR-74) - new tabbed pages */}
            <Route path="performance-hub/ibs" element={<P><PerformanceHubIBSPage /></P>} />
            <Route path="performance-hub/operational-components" element={<P><PerformanceHubOperationalComponentsPage /></P>} />
            <Route path="performance-hub/bulk-performance-upload" element={<P><PerformanceDataSubmissionPage /></P>} />
            {/* Configure Hub (OR-74 BR-002) */}
            <Route path="configure-hub/components" element={<P><ConfigureComponentsPage /></P>} />
            <Route path="configure-hub/ibs" element={<P><ConfigureIBSPage /></P>} />
            {/* Legacy route redirects -> Performance Hub */}
            {performanceHubRedirects()}
            {/* Legacy route redirects -> Configure Hub */}
            {configureHubRedirects()}
            <Route path="ibs/tree" element={<P><IBSTreeView /></P>} />
            <Route path="ibs/create" element={<P><IBSCreateWizard /></P>} />
            {/* /ibs/list was the old obsolete list page; redirect to the new IBS Dashboard tab. */}
            <Route path="ibs/list" element={<Navigate to="/performance-hub/ibs" replace />} />
            <Route path="ibs/:hubIbsId" element={<P><IBSDetailPage /></P>} />
            <Route path="ibs/:hubIbsId/edit" element={<P><IBSEditWizard /></P>} />
            {/* Resume an in-progress draft IBS in the creation wizard.
                Distinct from /edit so the Configure IBSs page can route
                draft rows to the multi-step wizard while published rows
                continue to use IBSEditWizard. */}
            <Route path="ibs/:hubIbsId/continue" element={<P><IBSCreateWizard /></P>} />
            {/* OR-323: standalone End-to-End Functional Flows page retired.
                Flow management now lives at Configure Hub > Configure IBSs >
                E2E Flows tab, and a per-IBS flows view is the E2E Flows tab
                on /ibs/:hubIbsId. The old route redirects to the detail page
                for any stale bookmarks. */}
            <Route path="ibs/:hubIbsId/flows" element={<RedirectIBSFlowsToDetail />} />
            <Route path="ibs/:hubIbsId/flows/create" element={<P><FlowBuilderWizard /></P>} />
            <Route path="ibs/:hubIbsId/flows/:flowId" element={<P><FlowViewPage /></P>} />
            <Route path="ibs/:hubIbsId/flows/:flowId/edit" element={<P><FlowBuilderWizard /></P>} />
            <Route path="ibs/:hubIbsId/microservices" element={<P><IBSMicroservicesPage /></P>} />
            <Route path="ibs/:hubIbsId/microservices-test" element={<P><TestMicroservicesPage /></P>} />
            <Route path="ibs/:hubIbsId/microservices/:microserviceId" element={<P><MicroserviceDetailPage /></P>} />
            <Route path="microservices/new" element={<P><MicroserviceFormPage /></P>} />
            <Route path="microservices/:id" element={<P><MicroserviceRegistryDetailPage /></P>} />
            <Route path="microservices/:id/edit" element={<P><MicroserviceFormPage /></P>} />
            <Route path="microservices/:id/performance" element={<P><MicroservicePerformanceView /></P>} />
            <Route path="systems-registry/new" element={<P><SystemForm /></P>} />
            <Route path="systems-registry/:id" element={<P><SystemDetailPage /></P>} />
            <Route path="systems-registry/:id/edit" element={<P><SystemForm /></P>} />
            <Route path="systems/:id/performance" element={<P><SystemPerformanceView /></P>} />
            <Route path="infrastructure-registry/new" element={<P><InfrastructureForm /></P>} />
            <Route path="infrastructure-registry/:id" element={<P><InfrastructureDetailPage /></P>} />
            <Route path="infrastructure-registry/:id/edit" element={<P><InfrastructureForm /></P>} />
            <Route path="infrastructure/:id/performance" element={<P><InfrastructurePerformanceView /></P>} />
            <Route path="teams-registry/:id" element={<P><TeamFormPage /></P>} />
            <Route path="teams/:id" element={<P><TeamDetailPage /></P>} />
            <Route path="locations-registry/:id" element={<P><LocationDetailPage /></P>} />
            <Route path="suppliers-registry/:id" element={<P><SupplierDetailPage /></P>} />
            <Route path="suppliers-registry/:id/performance" element={<P><SupplierPerformanceView /></P>} />
            <Route path="critical-data/:id" element={<P><CriticalDataDetailPage /></P>} />
            <Route path="relationships/:id" element={<P><RelationshipDetailPage /></P>} />
            <Route path="organization/resource-map" element={<P><OrganizationResourceMap /></P>} />
            <Route path="resources/linkage" element={<P><ResourceLinkagePage /></P>} />
            <Route path="scenarios/new" element={<P><ScenarioDefinitionPage /></P>} />
            <Route path="scenarios/wizard" element={<P><EnhancedScenarioWizard /></P>} />
            {/* OR-364: resume a saved wizard draft */}
            <Route path="scenarios/wizard/:scenarioId" element={<P><EnhancedScenarioWizard /></P>} />
            <Route path="scenarios/analyse" element={<P><CreateScenarioPage /></P>} />
            <Route path="scenarios/ibs-selection" element={<P><IBSAnalysisSelectionPage /></P>} />
            <Route path="scenarios/library" element={<P><ScenarioLibraryPage /></P>} />
            <Route path="scenarios/enhanced" element={<P><EnhancedScenariosPage /></P>} />
            <Route path="risks" element={<P><RisksPage /></P>} />
            <Route path="controls" element={<P><ControlsPage /></P>} />
            <Route path="treatment-actions" element={<P><TreatmentActionsPage /></P>} />
            <Route path="incidents" element={<P><IncidentsPage /></P>} />
            {/* CMORG Compliance Routes */}
            <Route path="scenarios/cmorg-library" element={<P><CMORGLibraryPage /></P>} />
            <Route path="vulnerabilities" element={<P><VulnerabilitiesPage /></P>} />
            <Route path="vulnerabilities/:id" element={<P><VulnerabilityDetailPage /></P>} />
            <Route path="critical-data/new" element={<P><CriticalDataFormPage /></P>} />
            <Route path="critical-data/:id/edit" element={<P><CriticalDataFormPage /></P>} />
            <Route path="lessons-learned" element={<P><LessonsLearnedPage /></P>} />
            <Route path="self-assessment" element={<P><SelfAssessmentPage /></P>} />
            {/* CMORG Gap Analysis Routes */}
            <Route path="resource-mappings" element={<P><ResourceMappingPage /></P>} />
            <Route path="resource-mappings/ibs/:hubIbsId" element={<P><IBSResourceMapPage /></P>} />
            <Route path="resource-mappings/concentration-risk" element={<P><ConcentrationRiskPage /></P>} />
            <Route path="scenarios/causation-matrix" element={<P><CausationImpactMatrixPage /></P>} />
            <Route path="scenarios/detail/:id" element={<P><ScenarioDetailPage /></P>} />
            {/* Knowledge Hub (Vault) */}
            <Route path="knowledge-hub" element={<P><VaultPage /></P>} />
            <Route path="knowledge-hub/documents/:id" element={<P><DocumentDetailPage /></P>} />
            <Route path="knowledge-hub/documents/:id/diff" element={<P><DocumentDiffPage /></P>} />
            <Route path="monitoring/performance" element={<P><PerformanceMonitoringForm /></P>} />
            <Route path="integrations" element={<P><IntegrationsPage /></P>} />
            <Route path="notifications" element={<P><NotificationsPage /></P>} />
            <Route path="help" element={<P><HelpPage /></P>} />
            {/* RBAC v1.2: Client Admin and activation routes */}
            <Route path="activate-admin" element={<P><ActivateAdminPage /></P>} />
            <Route path="client-admin" element={<Navigate to="/client-admin/users" replace />} />
            <Route path="client-admin/users" element={<P><UserManagementPage /></P>} />
            <Route path="client-admin/roles" element={<P><RoleManagementPage /></P>} />
            {/* Admin routes - require admin permission. Component-config base routes
                (microservices, systems, infrastructure, etc.) are now provided by
                configureHubRedirects() above; only Overview + detail/sub routes remain. */}
            <Route path="admin" element={<ProtectedRoute permissions={[Permission.ADMIN_VIEW_AUDIT]}><P><AdminDashboardPage /></P></ProtectedRoute>} />
            <Route path="admin/microservices/:id" element={<P><MicroserviceRegistryDetailPage /></P>} />
            <Route path="admin/systems/:id" element={<P><SystemDetailPage /></P>} />
            <Route path="admin/infrastructure/:id" element={<P><InfrastructureDetailPage /></P>} />
            <Route path="admin/teams/:id" element={<P><TeamFormPage /></P>} />
            <Route path="admin/suppliers/:id" element={<P><SupplierFormPage /></P>} />
            <Route path="admin/systems/:systemId/performance" element={<P><SystemPerformancePage /></P>} />
            <Route path="admin/microservices/:microserviceId/performance" element={<P><MicroservicePerformancePage /></P>} />
            <Route path="admin/infrastructure/:infrastructureId/performance" element={<P><InfrastructurePerformancePage /></P>} />
            <Route path="admin/ai-settings" element={<P><AISettingsPage /></P>} />
            <Route path="scenario-definition" element={<P><ScenarioDefinitionPage /></P>} />
            {/* Supplier Alternate - Sprint 19-21 */}
            <Route path="supplier-alternate" element={<P><SupplierAlternatePage /></P>} />
          </Route>

              {/* Catch all */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
              </ErrorBoundary>
            </ReportingPeriodProvider>
          </Auth0Provider>
        </AuthProvider>
      </TimePeriodProvider>
    </SidebarThemeProvider>
  )
}

export default App
